import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Package, Clock, Truck, CheckCircle, XCircle, CreditCard, Wifi, WifiOff } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { WhatsAppButton, WhatsAppMessages, WhatsAppLink } from '../components/WhatsAppChat';
import { useAuth } from '../contexts/AuthContext';
import { useOrderUpdates } from '../hooks/useOrderUpdates';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  const handleOrderUpdate = useCallback((data) => {
    if (data.type === 'order_status') {
      setOrders(prev => prev.map(o => 
        o.id === data.order_id 
          ? { ...o, status: data.status, updated_at: data.updated_at }
          : o
      ));
      const statusLabel = data.status.replace(/_/g, ' ');
      toast.info(`Order updated: ${statusLabel}`);
    }
  }, []);

  const { connected } = useOrderUpdates(token, handleOrderUpdate);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending_payment':
        return <CreditCard className="w-5 h-5 text-yellow-600" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'ready_for_pickup':
        return <Package className="w-5 h-5 text-purple-600" />;
      case 'out_for_delivery':
      case 'in_transit':
        return <Truck className="w-5 h-5 text-orange-600" />;
      case 'delivered':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'cancelled':
      case 'refunded':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Package className="w-5 h-5 text-stone-600" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending_payment':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
      case 'paid':
        return 'bg-blue-100 text-blue-800';
      case 'ready_for_pickup':
        return 'bg-purple-100 text-purple-800';
      case 'out_for_delivery':
      case 'in_transit':
      case 'picked_up':
        return 'bg-orange-100 text-orange-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
      case 'refunded':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-stone-100 text-stone-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePayOrder = async (orderId) => {
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        order_id: orderId,
        origin_url: window.location.origin
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Failed to initiate payment:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-stone-50" data-testid="orders-page">
      <Navbar />
      
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="heading-section text-stone-900" data-testid="orders-title">
              My Orders
            </h1>
            {connected && (
              <div className="flex items-center gap-1.5 text-xs text-green-600" data-testid="live-indicator">
                <Wifi className="w-3.5 h-3.5" />
                <span>Live updates</span>
              </div>
            )}
          </div>

          {orders.length === 0 ? (
            <div className="empty-state bg-white rounded-2xl py-16">
              <Package className="w-20 h-20 text-stone-300 mb-4" />
              <h3 className="text-xl font-semibold text-stone-900 mb-2">No orders yet</h3>
              <p className="text-stone-600">Start shopping to see your orders here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="bg-white rounded-xl p-6 shadow-sm"
                  data-testid={`order-${order.id}`}
                >
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-stone-200">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(order.status)}
                      <div>
                        <p className="font-semibold text-stone-900">
                          Order #{order.id.slice(0, 8)}
                        </p>
                        <p className="text-sm text-stone-500">
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(order.status)}`}>
                        {order.status.replace(/_/g, ' ').toUpperCase()}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(order.payment_status)}`}>
                        {order.payment_status.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="py-4 space-y-2">
                    {order.items.slice(0, 3).map((item, idx) => (
                      <div key={`${item.name}-${item.quantity}`} className="flex justify-between text-sm">
                        <span className="text-stone-600">
                          {item.quantity}x {item.name}
                        </span>
                        <span className="text-stone-900 font-medium">
                          R{item.subtotal.toFixed(2)}
                        </span>
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <p className="text-sm text-stone-500">
                        +{order.items.length - 3} more items
                      </p>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-4 border-t border-stone-200">
                    <div>
                      <p className="text-sm text-stone-500">Delivery Address:</p>
                      <p className="text-sm text-stone-900">{order.delivery_address}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-lg font-bold text-green-900">
                        R{order.total.toFixed(2)}
                      </p>
                      {order.payment_status === 'pending' && (
                        <Button
                          size="sm"
                          className="bg-orange-600 hover:bg-orange-700"
                          onClick={() => handlePayOrder(order.id)}
                          data-testid={`pay-order-${order.id}`}
                        >
                          Pay Now
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* WhatsApp Actions */}
                  <div className="mt-4 pt-4 border-t border-stone-100 flex flex-wrap gap-2">
                    <WhatsAppLink
                      message={WhatsAppMessages.orderStatus(order.id.slice(0, 8))}
                      className="text-sm"
                    >
                      Check Status
                    </WhatsAppLink>
                    {['out_for_delivery', 'in_transit'].includes(order.status) && (
                      <WhatsAppLink
                        phone={order.delivery_phone}
                        message={WhatsAppMessages.customerToDriver(order.id.slice(0, 8))}
                        className="text-sm"
                      >
                        Contact Driver
                      </WhatsAppLink>
                    )}
                    {order.payment_status === 'paid' && order.status !== 'delivered' && (
                      <WhatsAppLink
                        message={WhatsAppMessages.orderIssue(order.id.slice(0, 8))}
                        className="text-sm text-orange-600 hover:text-orange-700"
                      >
                        Report Issue
                      </WhatsAppLink>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OrdersPage;
